/**
 * Variant Slider Handler
 *
 * This script handles the initialization and reinitialization of variant sliders
 * when variants are selected in Shopify product pages.
 */

// Check if the handler is already defined to prevent duplicate declarations
if (!window.variantSliderHandler) {
  class VariantSliderHandler {
    constructor() {
      this.initialized = false;
      this.init();
    }

    init() {
      if (this.initialized) return;

      // Initialize on page load
      this.initAllVariantSliders();

      // Set up event listeners
      this.setupEventListeners();

      this.initialized = true;
    }

    setupEventListeners() {
      // Listen for Shopify's variant change event
      document.addEventListener('variant:change', this.handleVariantChange.bind(this));
      
      // Listen for Shopify section loads
      document.addEventListener('shopify:section:load', this.handleSectionLoad.bind(this));
      
      // Listen for variant selection clicks to prepare for transition
      document.addEventListener('click', this.handleVariantClick.bind(this));
    }

    handleVariantClick(event) {
      // Check if a variant option was clicked
      const variantInput = event.target.closest('.product-form__input input[type="radio"]');
      if (!variantInput) return;
      
      // Find the slider containing this variant
      const slider = variantInput.closest('.variant-swatch-slider');
      if (!slider || !slider.splide) return;
      
      // Store the selected variant ID in a data attribute on the slider
      // This will be used after refresh to smoothly transition
      const variantId = variantInput.value;
      slider.setAttribute('data-selected-variant', variantId);
      
      // Also store the option name for better tracking
      const optionName = slider.closest('fieldset')?.querySelector('.form__label')?.textContent.trim();
      if (optionName) {
        // Store in sessionStorage for persistence across page refreshes
        const storedSelections = JSON.parse(sessionStorage.getItem('selectedVariants') || '{}');
        storedSelections[optionName] = variantId;
        sessionStorage.setItem('selectedVariants', JSON.stringify(storedSelections));
      }
    }

    handleVariantChange() {
      
      // First, initialize all sliders without animation
      this.initAllVariantSliders();
      
      // Then smoothly scroll to selected variants with animation
      setTimeout(() => {
        this.smoothScrollToSelectedVariants();
      }, 100); // Short delay to ensure DOM is updated
    }

    handleSectionLoad() {
      
      // First, initialize all sliders without animation
      this.initAllVariantSliders();
      
      // Then smoothly scroll to selected variants with animation
      setTimeout(() => {
        this.smoothScrollToSelectedVariants();
      }, 100); // Short delay to ensure DOM is updated
    }

    smoothScrollToSelectedVariants() {
      const variantSliders = document.querySelectorAll('.variant-swatch-slider');
      const storedSelections = JSON.parse(sessionStorage.getItem('selectedVariants') || '{}');
      
      variantSliders.forEach(slider => {
        if (!slider.splide) return;
        
        // Get the option name for this slider
        const optionName = slider.closest('fieldset')?.querySelector('.form__label')?.textContent.trim();
        if (!optionName) return;
        
        // Find the selected radio button in this slider
        const selectedRadio = slider.querySelector('input[type="radio"]:checked');
        if (!selectedRadio) return;
        
        // Find the slide containing the selected radio
        const selectedSlide = selectedRadio.closest('.splide__slide');
        if (!selectedSlide) return;
        
        // Get the index of the selected slide
        const slideIndex = Array.from(slider.querySelectorAll('.splide__slide')).indexOf(selectedSlide);
        if (slideIndex < 0) return;
        
        // First, silently go to the slide without animation
        slider.splide.options = { ...slider.splide.options, speed: 0 };
        slider.splide.go(slideIndex);
        
        // Then restore animation speed for future interactions
        setTimeout(() => {
          slider.splide.options = { ...slider.splide.options, speed: 400 }; // Default animation speed
        }, 50);
        
      });
    }

    initAllVariantSliders() {
      const variantSliders = document.querySelectorAll('.variant-swatch-slider');;
      
      variantSliders.forEach((slider, index) => {
        this.initSingleVariantSlider(slider, index);
      });
    }

    initSingleVariantSlider(sliderElement, index) {
      try {
        // Check if this slider is already initialized with our custom attribute
        if (sliderElement.hasAttribute('data-slider-initialized')) {

          // If Splide has an instance on this element, destroy it first
          if (typeof Splide !== 'undefined' && sliderElement.splide) {
            sliderElement.splide.destroy(true);
          }

          sliderElement.removeAttribute('data-slider-initialized');
        }

        // Make sure Splide is available
        if (typeof Splide === 'undefined') {
          console.error('Splide library not found');
          return;
        }

        // Create a new Splide instance with edge-to-edge scrolling
        const variantSlider = new Splide(sliderElement, {
          type: 'slide',
          drag: true,
          snap: true,
          focus: 0,  // Focus on the first slide (left-aligned)
          pagination: false,
          perPage: 3,
          perMove: 1,
          autoWidth: true,
          speed: 400,
          easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
          dragMinThreshold: 0,
          waitForTransition: true,
          flickPower: 100,
          flickMaxPages: 1,
          wheelMinThreshold: 2,
          padding: { right: '0' },  // No padding to ensure edge-to-edge
          trimSpace: false,  // Don't trim space to allow full scrolling
          bound: true,  // Prevent scrolling beyond the last slide
          arrows: true
        });

        // Initialize the progress bar
        const progressBar = sliderElement.querySelector('.variant-swatch-splide-progress-bar');
        if (progressBar) {
          variantSlider.on('mounted move', function() {
            const end = variantSlider.Components.Controller.getEnd() + 1;
            const rate = Math.min((variantSlider.index + 1) / end, 1);
            progressBar.style.width = String(100 * rate) + '%';
          });
        }

        // Add event listener to ensure proper edge behavior
        variantSlider.on('moved', function() {
          const slideCount = variantSlider.Components.Elements.slides.length;
          const currentIndex = variantSlider.index;
          
          // If we're at the last slide, ensure it's properly positioned at the right edge
          if (currentIndex >= slideCount - 1) {
            // Get the track element and the last slide
            const track = variantSlider.Components.Elements.track;
            const lastSlide = variantSlider.Components.Elements.slides[slideCount - 1];
            
            // Calculate the maximum scroll position to ensure the last slide is at the right edge
            if (track && lastSlide) {
              const trackWidth = track.offsetWidth;
              const lastSlideWidth = lastSlide.offsetWidth;
              const lastSlideRight = lastSlide.offsetLeft + lastSlideWidth;
              
              // If the last slide is not at the right edge, adjust the position
              if (lastSlideRight < trackWidth) {
                const adjustment = trackWidth - lastSlideRight;
                variantSlider.Components.Controller.go(slideCount - 1, true);
              }
            }
          }
        });

        variantSlider.mount();
        sliderElement.setAttribute('data-slider-initialized', 'true');
      } catch (e) {
        console.error(`Error initializing variant slider ${index}:`, e);
      }
    }
  }

  // Initialize the handler when the DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    window.variantSliderHandler = new VariantSliderHandler();
  });
} else {
  // If the handler already exists, just reinitialize it
  document.addEventListener('DOMContentLoaded', () => {
    if (window.variantSliderHandler && typeof window.variantSliderHandler.init === 'function') {
      window.variantSliderHandler.init();
    }
  });
}

// Add progress bar functionality to variant slider
document.addEventListener('DOMContentLoaded', function() {
  const variantSliders = document.querySelectorAll('.variant-swatch-slider');
  
  variantSliders.forEach(slider => {
    // Create progress bar if it doesn't exist
    if (!slider.querySelector('.variant-slider-progress')) {
      const progressContainer = document.createElement('div');
      progressContainer.className = 'variant-slider-progress';
      
      const progressBar = document.createElement('div');
      progressBar.className = 'variant-slider-progress-bar';
      
      progressContainer.appendChild(progressBar);
      slider.appendChild(progressContainer);
    }
    
    // Wait for Splide to be initialized
    const checkSplide = setInterval(() => {
      if (slider.splide) {
        clearInterval(checkSplide);
        
        const bar = slider.querySelector('.variant-slider-progress-bar');
        
        // Updates the bar width whenever the carousel moves
        slider.splide.on('mounted move', function() {
          const end = slider.splide.Components.Controller.getEnd() + 1;
          const rate = Math.min((slider.splide.index + 1) / end, 1);
          bar.style.width = String(100 * rate) + '%';
        });
      }
    }, 100);
  });
});
